import { useEffect, useState } from 'react';
import {
  Calendar,
  Loader2,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Save,
  Trash2,
} from 'lucide-react';
import {
  createScheduleRule,
  deleteScheduleRule,
  getScheduleRules,
  toggleScheduleRule,
  updateScheduleRule,
} from '@/lib/api';
import type { CreateScheduleRuleForm, ScheduleRule } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn, normalizeBilibiliLiveUrl } from '@/lib/utils';

const DAYS_MAP: Record<number, string> = {
  1: '周一',
  2: '周二',
  3: '周三',
  4: '周四',
  5: '周五',
  6: '周六',
  7: '周日',
};

const DRAWER_CLASS =
  '!left-auto !right-0 !top-0 !h-[100dvh] !max-h-[100dvh] !w-full !max-w-[480px] !translate-x-0 !translate-y-0 !rounded-none !border-y-0 !border-r-0';

export default function ScheduleRulesPage() {
  const [rules, setRules] = useState<ScheduleRule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<ScheduleRule | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [pendingActionId, setPendingActionId] = useState<string | null>(null);

  const loadRules = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setRules(await getScheduleRules());
    } catch (loadError) {
      console.error('加载预约规则失败:', loadError);
      setError('加载预约规则失败，请刷新重试');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadRules();
  }, []);

  const handleRuleCreated = () => {
    void loadRules();
    setIsAddDialogOpen(false);
  };

  const handleRuleUpdated = () => {
    void loadRules();
    setIsEditDialogOpen(false);
    setEditingRule(null);
  };

  const handleEdit = (rule: ScheduleRule) => {
    setEditingRule(rule);
    setIsEditDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('确定要删除这条预约规则吗？删除后不会再自动创建场次。')) return;
    try {
      setPendingActionId(id);
      await deleteScheduleRule(id);
      await loadRules();
    } catch (deleteError) {
      console.error('删除规则失败:', deleteError);
      window.alert('删除失败，请重试');
    } finally {
      setPendingActionId(null);
    }
  };

  const handleToggle = async (id: string, currentStatus: boolean) => {
    try {
      setPendingActionId(id);
      await toggleScheduleRule(id, !currentStatus);
      await loadRules();
    } catch (toggleError) {
      console.error('切换规则状态失败:', toggleError);
      window.alert('操作失败，请重试');
    } finally {
      setPendingActionId(null);
    }
  };

  const formatDays = (days: number[]) => {
    if (days.length === 7) return '每天';
    if (days.length === 5 && [1, 2, 3, 4, 5].every((day) => days.includes(day))) return '工作日';
    if (days.length === 2 && [6, 7].every((day) => days.includes(day))) return '周末';
    return [...days].sort((a, b) => a - b).map((day) => DAYS_MAP[day]).join('、');
  };

  const formatTime = (timeStr: string) => timeStr.substring(0, 5);

  const formatNextTrigger = (dateStr?: string) => {
    if (!dateStr) return '未计算';
    const date = new Date(dateStr);
    const diffMs = date.getTime() - Date.now();
    if (Number.isNaN(date.getTime())) return '时间异常';
    if (diffMs < 0) return '等待重新计算';
    if (diffMs < 60 * 60 * 1000) return Math.max(1, Math.round(diffMs / 60000)) + ' 分钟后';
    if (diffMs < 24 * 60 * 60 * 1000) return Math.round(diffMs / 3600000) + ' 小时后';
    return date.toLocaleString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const enabledCount = rules.filter((rule) => rule.is_enabled).length;

  return (
    <div className="console-page">
      <header className="console-page-header">
        <div>
          <p className="console-kicker">SCHEDULE AUTOMATION</p>
          <h1 className="console-title">预约规则</h1>
          <p className="console-description">按固定周期自动创建直播场次，并提前准备采集服务。</p>
        </div>

        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="min-h-11 w-full sm:w-auto">
              <Plus className="h-4 w-4" />
              新建预约规则
            </Button>
          </DialogTrigger>
          <DialogContent className={DRAWER_CLASS}>
            <DialogHeader className="border-b border-[var(--structural-border)] pb-4 pr-8">
              <p className="console-kicker">NEW SCHEDULE RULE</p>
              <DialogTitle>新建预约规则</DialogTitle>
              <DialogDescription>设置直播间、重复周期与采集服务的启动和保护时长。</DialogDescription>
            </DialogHeader>
            <RuleForm mode="create" onSuccess={handleRuleCreated} />
          </DialogContent>
        </Dialog>
      </header>

      <Dialog
        open={isEditDialogOpen}
        onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) setEditingRule(null);
        }}
      >
        <DialogContent className={DRAWER_CLASS}>
          <DialogHeader className="border-b border-[var(--structural-border)] pb-4 pr-8">
            <p className="console-kicker">EDIT SCHEDULE RULE</p>
            <DialogTitle>编辑预约规则</DialogTitle>
            <DialogDescription>调整当前规则的执行周期、启动时间与采集保护参数。</DialogDescription>
          </DialogHeader>
          {editingRule && <RuleForm mode="edit" rule={editingRule} onSuccess={handleRuleUpdated} />}
        </DialogContent>
      </Dialog>

      {error && (
        <div role="alert" className="console-alert-error px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <section className="console-panel" aria-labelledby="rules-list-title">
        <div className="console-panel-header">
          <div className="flex items-baseline gap-3">
            <h2 id="rules-list-title" className="text-sm font-bold text-[var(--text-ink)]">规则列表</h2>
            <p className="font-mono text-[11px] text-[var(--text-muted)]">
              {rules.length} 条 · {enabledCount} 条启用
            </p>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={() => void loadRules()} disabled={isLoading}>
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            刷新
          </Button>
        </div>

        {isLoading ? (
          <div className="flex min-h-56 items-center justify-center gap-3 text-sm text-[var(--text-muted)]">
            <Loader2 className="h-5 w-5 animate-spin text-[var(--action-blue)]" />
            正在读取规则
          </div>
        ) : rules.length === 0 ? (
          <div className="flex min-h-56 flex-col items-center justify-center px-5 text-center">
            <Calendar className="h-8 w-8 text-[var(--border-strong)]" />
            <h3 className="mt-3 text-sm font-semibold text-[var(--text-soft)]">暂无预约规则</h3>
            <p className="mt-1 text-xs text-[var(--text-muted)]">创建规则后，系统会按周期自动准备场次。</p>
            <Button type="button" onClick={() => setIsAddDialogOpen(true)} className="mt-4">
              <Plus className="h-4 w-4" />创建第一条规则
            </Button>
          </div>
        ) : (
          <>
            <div className="hidden xl:block">
              <div className="grid grid-cols-[98px_minmax(180px,1fr)_160px_150px_210px_150px] items-center border-b border-[var(--border-subtle)] px-[18px] py-2.5 font-mono text-[10px] tracking-[0.12em] text-[var(--text-muted)]">
                <span>状态</span><span>规则 / 房间</span><span>周期</span><span>下次触发</span><span>采集保护</span><span className="text-right">操作</span>
              </div>
              <div className="divide-y divide-[var(--border-subtle)]">
                {rules.map((rule) => (
                  <article
                    key={rule.id}
                    className="grid min-h-[62px] grid-cols-[98px_minmax(180px,1fr)_160px_150px_210px_150px] items-center px-[18px] transition-colors hover:bg-[var(--surface-hover)]"
                  >
                    <span className={cn(
                      'flex items-center gap-2 text-xs font-semibold',
                      rule.is_enabled ? 'text-[var(--status-healthy)]' : 'text-[var(--status-unknown)]'
                    )}>
                      <span className={cn('h-1.5 w-1.5 rounded-full', rule.is_enabled ? 'bg-[var(--status-healthy)]' : 'bg-[var(--status-unknown)]')} />
                      {rule.is_enabled ? '已启用' : '已停用'}
                    </span>
                    <div className="min-w-0 pr-4">
                      <h3 className="truncate text-[13px] font-medium text-[var(--text-ink)]">{rule.name || '房间 ' + rule.room_id}</h3>
                      <p className="mt-1 font-mono text-[11px] text-[var(--text-muted)]">{rule.room_id} · {rule.room_url}</p>
                    </div>
                    <div className="text-xs text-[var(--text-soft)]">
                      <p>{formatDays(rule.days_of_week)}</p>
                      <p className="mt-1 font-mono text-[var(--text-muted)]">{formatTime(rule.start_time)}</p>
                    </div>
                    <p className={cn('font-mono text-xs', rule.is_enabled ? 'text-[var(--action-blue)]' : 'text-[var(--text-muted)]')}>
                      {rule.is_enabled ? formatNextTrigger(rule.next_trigger_at) : '—'}
                    </p>
                    <p className="text-xs leading-5 text-[var(--text-muted)]">
                      提前 {rule.advance_minutes} 分 · WS {rule.ws_timeout_minutes} 分<br />
                      最短保护 {rule.min_duration_minutes} 分
                    </p>
                    <div className="flex justify-end gap-1">
                      <Button type="button" variant="ghost" size="icon" onClick={() => handleEdit(rule)} aria-label={'编辑 ' + (rule.name || '预约规则')}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => void handleToggle(rule.id, rule.is_enabled)}
                        disabled={pendingActionId === rule.id}
                        aria-label={rule.is_enabled ? '停用规则' : '启用规则'}
                      >
                        {rule.is_enabled ? <PowerOff className="h-4 w-4 text-[var(--status-warning)]" /> : <Power className="h-4 w-4 text-[var(--status-healthy)]" />}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => void handleDelete(rule.id)}
                        disabled={pendingActionId === rule.id}
                        aria-label={'删除 ' + (rule.name || '预约规则')}
                        className="hover:bg-[var(--alert-error-bg)] hover:text-[var(--status-error)]"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <div className="divide-y divide-[var(--border-subtle)] xl:hidden">
              {rules.map((rule) => (
                <article key={rule.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className={cn('flex items-center gap-2 text-xs font-semibold', rule.is_enabled ? 'text-[var(--status-healthy)]' : 'text-[var(--status-unknown)]')}>
                        <span className={cn('h-1.5 w-1.5 rounded-full', rule.is_enabled ? 'bg-[var(--status-healthy)]' : 'bg-[var(--status-unknown)]')} />
                        {rule.is_enabled ? '已启用' : '已停用'}
                      </p>
                      <h3 className="mt-2 truncate text-sm font-semibold text-[var(--text-ink)]">{rule.name || '房间 ' + rule.room_id}</h3>
                      <p className="mt-1 font-mono text-[11px] text-[var(--text-muted)]">房间 {rule.room_id}</p>
                    </div>
                    <p className="shrink-0 text-right font-mono text-xs text-[var(--action-blue)]">
                      {rule.is_enabled ? formatNextTrigger(rule.next_trigger_at) : '已停用'}
                    </p>
                  </div>
                  <dl className="mt-4 grid grid-cols-2 gap-3 rounded-md border border-[var(--border-subtle)] bg-[var(--panel-deep)] p-3 text-xs">
                    <div><dt className="text-[var(--text-muted)]">执行周期</dt><dd className="mt-1 text-[var(--text-soft)]">{formatDays(rule.days_of_week)} · {formatTime(rule.start_time)}</dd></div>
                    <div><dt className="text-[var(--text-muted)]">提前启动</dt><dd className="mt-1 text-[var(--text-soft)]">{rule.advance_minutes} 分钟</dd></div>
                    <div><dt className="text-[var(--text-muted)]">WS 超时</dt><dd className="mt-1 text-[var(--text-soft)]">{rule.ws_timeout_minutes} 分钟</dd></div>
                    <div><dt className="text-[var(--text-muted)]">最短保护</dt><dd className="mt-1 text-[var(--text-soft)]">{rule.min_duration_minutes} 分钟</dd></div>
                  </dl>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <Button type="button" variant="outline" onClick={() => handleEdit(rule)}><Pencil className="h-4 w-4" />编辑</Button>
                    <Button type="button" variant="outline" onClick={() => void handleToggle(rule.id, rule.is_enabled)} disabled={pendingActionId === rule.id}>
                      {rule.is_enabled ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                      {rule.is_enabled ? '停用' : '启用'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => void handleDelete(rule.id)} disabled={pendingActionId === rule.id} className="hover:border-[var(--danger-border)] hover:bg-[var(--alert-error-bg)] hover:text-[var(--status-error)]">
                      <Trash2 className="h-4 w-4" />删除
                    </Button>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
      </section>

      <aside className="console-panel p-4 text-xs leading-6 text-[var(--text-muted)]">
        <p className="console-kicker">HOW IT WORKS · 运行说明</p>
        <p className="mt-2">
          规则到点后自动创建直播场次；若直播间已开播会立即进入采集。后端调度服务需要保持运行，首页会展示下一次可确认的触发时间。
        </p>
      </aside>
    </div>
  );
}

interface RuleFormProps {
  mode: 'create' | 'edit';
  rule?: ScheduleRule;
  onSuccess: () => void;
}

function RuleForm({ mode, rule, onSuccess }: RuleFormProps) {
  const isEditMode = mode === 'edit';
  const [formData, setFormData] = useState<CreateScheduleRuleForm>({
    room_url: rule?.room_url || '',
    name: rule?.name || '',
    days_of_week: rule?.days_of_week || [1, 2, 3, 4, 5],
    start_time: rule?.start_time?.substring(0, 5) || '14:00',
    advance_minutes: rule?.advance_minutes || 1,
    ws_timeout_minutes: rule?.ws_timeout_minutes ?? 5,
    min_duration_minutes: rule?.min_duration_minutes ?? 10,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDayToggle = (day: number) => {
    setFormData((previous) => ({
      ...previous,
      days_of_week: previous.days_of_week.includes(day)
        ? previous.days_of_week.filter((item) => item !== day)
        : [...previous.days_of_week, day].sort((a, b) => a - b),
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formData.room_url.trim()) {
      setError('请输入直播间 URL');
      return;
    }
    const normalizedUrl = normalizeBilibiliLiveUrl(formData.room_url);
    if (!normalizedUrl) {
      setError('请输入有效的 B 站直播间链接');
      return;
    }
    if (formData.days_of_week.length === 0) {
      setError('请至少选择一天');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      if (isEditMode && rule) {
        await updateScheduleRule(rule.id, {
          name: formData.name || undefined,
          days_of_week: formData.days_of_week,
          start_time: formData.start_time + ':00',
          advance_minutes: formData.advance_minutes,
          ws_timeout_minutes: formData.ws_timeout_minutes,
          min_duration_minutes: formData.min_duration_minutes,
        });
      } else {
        await createScheduleRule({ ...formData, room_url: normalizedUrl });
      }
      onSuccess();
    } catch (submitError) {
      console.error(isEditMode ? '更新规则失败:' : '创建规则失败:', submitError);
      setError(submitError instanceof Error ? submitError.message : isEditMode ? '更新失败，请重试' : '创建失败，请重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 pb-2">
      <div className="space-y-2">
        <Label htmlFor="room_url">直播间 URL <span className="text-[var(--status-error)]">*</span></Label>
        <Input
          id="room_url"
          value={formData.room_url}
          onChange={(event) => setFormData((previous) => ({ ...previous, room_url: event.target.value }))}
          placeholder="https://live.bilibili.com/123456"
          disabled={isSubmitting || isEditMode}
        />
        {isEditMode && <p className="text-xs text-[var(--text-muted)]">编辑模式下不支持修改直播间。</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="name">规则名称（可选）</Label>
        <Input id="name" value={formData.name} onChange={(event) => setFormData((previous) => ({ ...previous, name: event.target.value }))} placeholder="例如：育碧中国主频道" disabled={isSubmitting} />
      </div>

      <fieldset>
        <legend className="text-sm font-medium">重复周期 <span className="text-[var(--status-error)]">*</span></legend>
        <div className="mt-2 grid grid-cols-4 gap-2 sm:grid-cols-7">
          {[1, 2, 3, 4, 5, 6, 7].map((day) => (
            <button
              key={day}
              type="button"
              onClick={() => handleDayToggle(day)}
              disabled={isSubmitting}
              aria-pressed={formData.days_of_week.includes(day)}
              className={cn(
                'min-h-11 rounded-md border px-2 text-xs font-medium transition-colors',
                formData.days_of_week.includes(day)
                  ? 'border-[var(--action-blue)] bg-[var(--action-fill)] text-[var(--action-contrast)]'
                  : 'border-[var(--border-control)] bg-[var(--panel-deep)] text-[var(--text-muted)] hover:text-[var(--text-soft)]'
              )}
            >
              {DAYS_MAP[day].replace('周', '')}
            </button>
          ))}
        </div>
        <div className="mt-2 flex gap-4">
          <button type="button" onClick={() => setFormData((previous) => ({ ...previous, days_of_week: [1, 2, 3, 4, 5] }))} className="min-h-9 text-xs text-[var(--action-blue)]">工作日</button>
          <button type="button" onClick={() => setFormData((previous) => ({ ...previous, days_of_week: [6, 7] }))} className="min-h-9 text-xs text-[var(--action-blue)]">周末</button>
          <button type="button" onClick={() => setFormData((previous) => ({ ...previous, days_of_week: [1, 2, 3, 4, 5, 6, 7] }))} className="min-h-9 text-xs text-[var(--action-blue)]">每天</button>
        </div>
      </fieldset>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="start_time">开始时间</Label>
          <Input id="start_time" type="time" value={formData.start_time} onChange={(event) => setFormData((previous) => ({ ...previous, start_time: event.target.value }))} disabled={isSubmitting} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="advance_minutes">提前启动（分钟）</Label>
          <Input id="advance_minutes" type="number" min={1} max={60} value={formData.advance_minutes} onChange={(event) => setFormData((previous) => ({ ...previous, advance_minutes: Number.parseInt(event.target.value, 10) || 1 }))} disabled={isSubmitting} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="ws_timeout_minutes">WS 等待超时</Label>
          <Input id="ws_timeout_minutes" type="number" min={1} max={180} value={formData.ws_timeout_minutes ?? 5} onChange={(event) => setFormData((previous) => ({ ...previous, ws_timeout_minutes: Number.parseInt(event.target.value, 10) || 5 }))} disabled={isSubmitting} />
          <p className="text-[11px] leading-5 text-[var(--text-muted)]">等待首条弹幕的最长分钟数。</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="min_duration_minutes">最短保护时长</Label>
          <Input id="min_duration_minutes" type="number" min={1} max={180} value={formData.min_duration_minutes ?? 10} onChange={(event) => setFormData((previous) => ({ ...previous, min_duration_minutes: Number.parseInt(event.target.value, 10) || 10 }))} disabled={isSubmitting} />
          <p className="text-[11px] leading-5 text-[var(--text-muted)]">收到结束信号后的最短运行分钟数。</p>
        </div>
      </div>

      {error && <div role="alert" className="console-alert-error px-4 py-3 text-sm">{error}</div>}

      <Button type="submit" disabled={isSubmitting} className="w-full min-h-11">
        {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : isEditMode ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        {isSubmitting ? (isEditMode ? '保存中' : '创建中') : isEditMode ? '保存修改' : '创建规则'}
      </Button>
    </form>
  );
}
